import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { Flag, Delete, Visibility, MoreVert } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { isAdmin } from '../../utils/adminUtils';
import { blockUserService } from '../../services/blockUserService';

const client = generateClient();

const ReportsPage = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [postReports, setPostReports] = useState([]);
  const [userReports, setUserReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reporterDetails, setReporterDetails] = useState(null);
  const [reportedUserDetails, setReportedUserDetails] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [reportType, setReportType] = useState(0); // 0 = Post Reports, 1 = User Reports
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const statusFilters = ['PENDING', 'REVIEWED', 'RESOLVED', 'DISMISSED'];

  useEffect(() => {
    if (isAdmin(user)) {
      fetchReports();
    }
  }, [user]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      
      // Fetch post reports
      const postResult = await client.graphql({
        query: `
          query ListPostReports {
            listPostReports(limit: 100) {
              items {
                id
                postID
                reportedBy
                reason
                description
                status
                createdAt
                post {
                  id
                  content
                  author {
                    id
                    displayName
                    username
                  }
                }
                reporter {
                  id
                  displayName
                  username
                }
              }
            }
          }
        `,
      });
      
      // Fetch user reports
      const userResult = await client.graphql({
        query: `
          query ListUserReports {
            listUserReports(limit: 100) {
              items {
                id
                reporterID
                reportedUserID
                reason
                description
                category
                status
                createdAt
                updatedAt
              }
            }
          }
        `,
      });
      
      setPostReports(postResult.data.listPostReports.items || []);
      setUserReports(userResult.data.listUserReports.items || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load reports',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateReportStatus = async (reportId, status) => {
    try {
      const currentReports = reportType === 0 ? postReports : userReports;
      const report = currentReports.find(r => r.id === reportId);
      
      if (reportType === 0) {
        // Update post report
        await client.graphql({
          query: `
            mutation UpdatePostReport($input: UpdatePostReportInput!) {
              updatePostReport(input: $input) {
                id
                status
              }
            }
          `,
          variables: {
            input: { id: reportId, status }
          }
        });
        
        setPostReports(prev => prev.map(report => 
          report.id === reportId ? { ...report, status } : report
        ));
      } else {
        // Update user report
        await client.graphql({
          query: `
            mutation UpdateUserReport($input: UpdateUserReportInput!) {
              updateUserReport(input: $input) {
                id
                status
              }
            }
          `,
          variables: {
            input: { id: reportId, status }
          }
        });
        
        setUserReports(prev => prev.map(report => 
          report.id === reportId ? { ...report, status } : report
        ));
      }
      
      // Send notification to reporter about status change
      const reporterID = report?.reportedBy || report?.reporterID;
      if (reporterID) {
        await client.graphql({
          query: `
            mutation CreateNotification($input: CreateNotificationInput!) {
              createNotification(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: {
              userID: reporterID,
              type: status === 'RESOLVED' ? 'SUCCESS' : 'INFO',
              message: `Your ${reportType === 0 ? 'post' : 'user'} report has been ${status.toLowerCase()}.`,
              isRead: false,
              metadata: JSON.stringify({ reportId, type: `${reportType === 0 ? 'post' : 'user'}_report_status` })
            }
          }
        });
      }
      
      addNotification({
        type: 'success',
        message: `Report marked as ${status.toLowerCase()}`,
      });
    } catch (error) {
      console.error('Error updating report:', error);
      addNotification({
        type: 'error',
        message: 'Failed to update report status',
      });
    }
  };

  const deletePost = async (postId) => {
    try {
      // Get post details first to notify author
      const postResult = await client.graphql({
        query: `
          query GetPost($id: ID!) {
            getPost(id: $id) {
              id
              authorID
              content
            }
          }
        `,
        variables: { id: postId }
      });
      
      await client.graphql({
        query: `
          mutation DeletePost($input: DeletePostInput!) {
            deletePost(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: { id: postId }
        }
      });
      
      // Notify post author about deletion
      if (postResult.data.getPost?.authorID) {
        await client.graphql({
          query: `
            mutation CreateNotification($input: CreateNotificationInput!) {
              createNotification(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: {
              userID: postResult.data.getPost.authorID,
              type: 'ERROR',
              message: 'Your post has been removed by administrators due to policy violations.',
              isRead: false,
              metadata: JSON.stringify({ postId, type: 'post_deleted' })
            }
          }
        });
      }
      
      addNotification({
        type: 'success',
        message: 'Post deleted successfully',
      });
      
      fetchReports();
    } catch (error) {
      console.error('Error deleting post:', error);
      addNotification({
        type: 'error',
        message: 'Failed to delete post',
      });
    }
  };

  const fetchUserReportDetails = async (report) => {
    if (reportType === 0) return; // Only for user reports
    
    setLoadingDetails(true);
    try {
      // Fetch reporter details
      const reporterResult = await client.graphql({
        query: `
          query GetUser($id: ID!) {
            getUser(id: $id) {
              id
              username
              displayName
              email
              bio
              website
              avatar
              role
              createdAt
              followersCount
              followingCount
              postsCount
            }
          }
        `,
        variables: { id: report.reporterID }
      });
      
      // Fetch reported user details
      const reportedResult = await client.graphql({
        query: `
          query GetUser($id: ID!) {
            getUser(id: $id) {
              id
              username
              displayName
              email
              bio
              website
              avatar
              role
              createdAt
              followersCount
              followingCount
              postsCount
            }
          }
        `,
        variables: { id: report.reportedUserID }
      });
      
      // Fetch conversation messages between the two users
      const messagesResult = await client.graphql({
        query: `
          query ListMessages {
            listMessages(
              limit: 20
              filter: {
                or: [
                  {
                    and: [
                      { senderID: { eq: "${report.reporterID}" } }
                      { receiverID: { eq: "${report.reportedUserID}" } }
                    ]
                  }
                  {
                    and: [
                      { senderID: { eq: "${report.reportedUserID}" } }
                      { receiverID: { eq: "${report.reporterID}" } }
                    ]
                  }
                ]
              }
            ) {
              items {
                id
                content
                senderID
                receiverID
                createdAt
                sender {
                  displayName
                  username
                }
              }
            }
          }
        `
      });
      
      setReporterDetails(reporterResult.data.getUser);
      setReportedUserDetails(reportedResult.data.getUser);
      setConversationMessages(messagesResult.data.listMessages.items || []);
    } catch (error) {
      console.error('Error fetching user details:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load user details',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewPost = async (report) => {
    setSelectedReport(report);
    setDetailsOpen(true);
    
    if (reportType === 1) {
      await fetchUserReportDetails(report);
    }
  };

  const handleMenuClick = (event, reportId) => {
    setAnchorEl(event.currentTarget);
    setSelectedReportId(reportId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReportId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'REVIEWED': return 'info';
      case 'RESOLVED': return 'success';
      case 'DISMISSED': return 'default';
      default: return 'default';
    }
  };

  const currentReports = reportType === 0 ? postReports : userReports;
  const filteredReports = currentReports.filter(report => {
    if (activeTab === 0) return true;
    return report.status === statusFilters[activeTab - 1];
  });

  if (!isAdmin(user)) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Reports Management
      </Typography>

      {/* Report Type Tabs */}
      <Tabs value={reportType} onChange={(e, newValue) => setReportType(newValue)} sx={{ mb: 2 }}>
        <Tab label={`Post Reports (${postReports.length})`} />
        <Tab label={`User Reports (${userReports.length})`} />
      </Tabs>

      {/* Status Filter Tabs */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={`All (${currentReports.length})`} />
        {statusFilters.map((status, index) => (
          <Tab 
            key={status} 
            label={`${status} (${currentReports.filter(r => r.status === status).length})`} 
          />
        ))}
      </Tabs>

      {filteredReports.length === 0 ? (
        <Alert severity="info">No reports found.</Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredReports.map((report) => (
            <Grid item xs={12} key={report.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Chip 
                      icon={<Flag />}
                      label={report.status}
                      color={getStatusColor(report.status)}
                      size="small"
                    />
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuClick(e, report.id)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    {report.reason}
                  </Typography>

                  {reportType === 0 ? (
                    // Post Report Display
                    <>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Reported by: {report.reporter?.displayName || 'Unknown User'} (ID: {report.reportedBy})
                      </Typography>

                      <Typography variant="body2" color="text.secondary" paragraph>
                        Post by: {report.post?.author?.displayName || 'Unknown User'} (ID: {report.post?.authorID})
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {report.post?.content?.slice(0, 100)}
                        {report.post?.content?.length > 100 && '...'}
                      </Typography>
                    </>
                  ) : (
                    // User Report Display
                    <>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Reporter: {reporterDetails?.displayName || 'Loading...'} (ID: {report.reporterID})
                      </Typography>

                      <Typography variant="body2" color="text.secondary" paragraph>
                        Reported User: {reportedUserDetails?.displayName || 'Loading...'} (ID: {report.reportedUserID})
                      </Typography>

                      <Typography variant="body2" color="text.secondary" paragraph>
                        Category: {report.category}
                      </Typography>
                    </>
                  )}

                  {report.description && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Details: {report.description}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(report.createdAt))} ago
                  </Typography>
                </CardContent>

                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<Visibility />}
                    onClick={() => handleViewPost(report)}
                  >
                    {reportType === 0 ? 'View Post' : 'View Details'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem key="reviewed" onClick={() => {
          updateReportStatus(selectedReportId, 'REVIEWED');
          handleMenuClose();
        }}>
          Mark as Reviewed
        </MenuItem>
        <MenuItem key="resolved" onClick={() => {
          updateReportStatus(selectedReportId, 'RESOLVED');
          handleMenuClose();
        }}>
          Mark as Resolved
        </MenuItem>
        <MenuItem key="dismissed" onClick={() => {
          updateReportStatus(selectedReportId, 'DISMISSED');
          handleMenuClose();
        }}>
          Dismiss Report
        </MenuItem>
        {reportType === 0 && (
          <MenuItem 
            key="delete"
            onClick={() => {
              const report = postReports.find(r => r.id === selectedReportId);
              if (report?.post?.id) {
                deletePost(report.post.id);
              }
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            Delete Post
          </MenuItem>
        )}
      </Menu>

      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{reportType === 0 ? 'Post Report Details' : 'User Report Details'}</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box>
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    📋 Report Information
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Reason:</strong> {selectedReport.reason}
                    </Typography>
                    <Box>
                      <strong>Status:</strong>
                      <Chip 
                        label={selectedReport.status} 
                        color={getStatusColor(selectedReport.status)} 
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    {selectedReport.category && (
                      <Typography variant="body2">
                        <strong>Category:</strong> {selectedReport.category}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Date:</strong> {new Date(selectedReport.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  {selectedReport.description && (
                    <Typography variant="body2">
                      <strong>Description:</strong> {selectedReport.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {reportType === 0 ? (
                <>
                  <Card variant="outlined" sx={{ bgcolor: 'info.light' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="info.dark">
                        📝 Reported Post
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Author:</strong> {selectedReport.post?.author?.displayName} (ID: {selectedReport.post?.authorID})
                      </Typography>
                      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                        <Typography variant="body1">
                          {selectedReport.post?.content}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <>
                  {loadingDetails ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      {/* Reporter Details */}
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Reporter Details
                      </Typography>
                      {reporterDetails && (
                        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'success.light' }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                              👤 Reporter Information
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                              <Typography variant="body2"><strong>ID:</strong> {reporterDetails.id}</Typography>
                              <Typography variant="body2"><strong>Username:</strong> {reporterDetails.username}</Typography>
                              <Typography variant="body2"><strong>Display:</strong> {reporterDetails.displayName}</Typography>
                              <Typography variant="body2"><strong>Email:</strong> {reporterDetails.email}</Typography>
                              <Typography variant="body2"><strong>Role:</strong> {reporterDetails.role}</Typography>
                              <Typography variant="body2"><strong>Joined:</strong> {new Date(reporterDetails.createdAt).toLocaleDateString()}</Typography>
                            </Box>
                            <Typography variant="body2">
                              <strong>Stats:</strong> {reporterDetails.postsCount} posts, {reporterDetails.followersCount} followers
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Reported User Details */}
                      <Typography variant="h6" gutterBottom>
                        Reported User Details
                      </Typography>
                      {reportedUserDetails && (
                        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'warning.light' }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                              ⚠️ Reported User Information
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                              <Typography variant="body2"><strong>ID:</strong> {reportedUserDetails.id}</Typography>
                              <Typography variant="body2"><strong>Username:</strong> {reportedUserDetails.username}</Typography>
                              <Typography variant="body2"><strong>Display:</strong> {reportedUserDetails.displayName}</Typography>
                              <Typography variant="body2"><strong>Email:</strong> {reportedUserDetails.email}</Typography>
                              <Typography variant="body2"><strong>Role:</strong> {reportedUserDetails.role}</Typography>
                              <Typography variant="body2"><strong>Joined:</strong> {new Date(reportedUserDetails.createdAt).toLocaleDateString()}</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: reportedUserDetails.bio ? 1 : 0 }}>
                              <strong>Stats:</strong> {reportedUserDetails.postsCount} posts, {reportedUserDetails.followersCount} followers
                            </Typography>
                            {reportedUserDetails.bio && (
                              <Typography variant="body2">
                                <strong>Bio:</strong> {reportedUserDetails.bio}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Recent Conversation */}
                      <Typography variant="h6" gutterBottom>
                        Recent Conversation (Last 10 Messages)
                      </Typography>
                      {conversationMessages.length > 0 ? (
                        <Card variant="outlined" sx={{ maxHeight: 400, overflow: 'hidden' }}>
                          <CardContent sx={{ p: 0 }}>
                            <Box sx={{ maxHeight: 400, overflow: 'auto', p: 2 }}>
                              {conversationMessages
                                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                .slice(-10)
                                .map((message) => (
                                <Card 
                                  key={message.id} 
                                  variant="outlined"
                                  sx={{ 
                                    mb: 1, 
                                    bgcolor: message.senderID === selectedReport.reporterID ? 'success.light' : 'warning.light',
                                    borderLeft: 4,
                                    borderLeftColor: message.senderID === selectedReport.reporterID ? 'success.main' : 'warning.main'
                                  }}
                                >
                                  <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                                        {message.senderID === selectedReport.reporterID ? '👤 Reporter' : '⚠️ Reported User'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        ({message.sender?.displayName || 'Unknown'}) • {new Date(message.createdAt).toLocaleString()}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                      {message.content}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      ) : (
                        <Alert severity="info">No conversation messages found between these users.</Alert>
                      )}
                      
                      <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                        <strong>Report Date:</strong> {new Date(selectedReport.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          {selectedReport && (
            <Box>
              <Button 
                onClick={() => {
                  updateReportStatus(selectedReport.id, 'RESOLVED');
                  setDetailsOpen(false);
                }}
                color="success"
              >
                Resolve
              </Button>
              {reportType === 0 ? (
                <Button 
                  onClick={() => {
                    deletePost(selectedReport.post?.id);
                    setDetailsOpen(false);
                  }}
                  color="error"
                >
                  Delete Post
                </Button>
              ) : (
                <>
                  <Button 
                    onClick={async () => {
                      try {
                        // Create notification for suspended user
                        await client.graphql({
                          query: `
                            mutation CreateNotification($input: CreateNotificationInput!) {
                              createNotification(input: $input) {
                                id
                              }
                            }
                          `,
                          variables: {
                            input: {
                              userID: selectedReport.reportedUserID,
                              type: 'ERROR',
                              message: 'Your account has been temporarily suspended due to policy violations. Contact support for more information.',
                              isRead: false,
                              metadata: JSON.stringify({ 
                                reportId: selectedReport.id, 
                                type: 'user_suspended',
                                reason: selectedReport.reason
                              })
                            }
                          }
                        });
                        
                        await updateReportStatus(selectedReport.id, 'RESOLVED');
                        
                        addNotification({
                          type: 'success',
                          message: 'User suspended successfully',
                        });
                        
                        setDetailsOpen(false);
                      } catch (error) {
                        addNotification({
                          type: 'error',
                          message: 'Failed to suspend user',
                        });
                      }
                    }}
                    color="warning"
                  >
                    Suspend User
                  </Button>
                  <Button 
                    onClick={async () => {
                      try {
                        const result = await blockUserService.blockUser(
                          selectedReport.reportedUserID,
                          `Admin action: Report ${selectedReport.id} - ${selectedReport.reason}`
                        );
                        
                        if (result.success) {
                          await updateReportStatus(selectedReport.id, 'RESOLVED');
                          
                          addNotification({
                            type: 'success',
                            message: 'User blocked successfully',
                          });
                          
                          setDetailsOpen(false);
                        } else {
                          throw new Error(result.message);
                        }
                      } catch (error) {
                        addNotification({
                          type: 'error',
                          message: error.message || 'Failed to block user',
                        });
                      }
                    }}
                    color="error"
                  >
                    Block User
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportsPage;